/**
 * RWBR — THE LAPS-DOWN SUB-GROUP (LDSG).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE BOTTOM GROUP SPLITS IN TWO, AND THE SPLIT IS A POSITION, NOT A FLAG.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The BOTTOM group becomes two sub-groups: the **LLSG** (lead-lap sub-group)
 * and the **LDSG** (laps-down sub-group). EZRA describes the split physically,
 * as a marker card sitting between two positions on the BOTTOM group display:
 *
 *   > "Last position of 'lead lap' sub-group is 38th. The First position of
 *   >  'laps down' sub-group (and marked with the yellow card) is position 39th.
 *   >  The yellow flag comes out and the yellow card is moved to position 40th,
 *   >  marking it (and the occupying driver) as first position in the 'laps
 *   >  down' sub-group. Position 39th … is now the last position on the
 *   >  'lead-lap' sub-group."
 *
 * That example is the whole data model. **The LDSG is a SUFFIX of the running
 * order** — every lapped car sorts below every lead-lap car, always. Zimmerman's
 * original sheet says why in as many words:
 *
 *   > "Doing it this way will preserve the game's 'King of the Hill'
 *   >  methodology, in that cars are ranked in order at all times during the
 *   >  race. (Under Dennis' original method, a 'laps down' driver might occupy a
 *   >  position in the MIDDLE or even TOP group, requiring some sort of notation
 *   >  on the score sheet or driver card.)"
 *
 * So `Car.lapsDown` is **derived bookkeeping, not the authority**. The authority
 * is the boundary — `ldsgStart(order)`. Keeping it a suffix is what lets the
 * rest of the engine stay untouched: `fieldFromState` still sees a contiguous
 * 1..n grid, `groupOf` still answers from a position, and the boards still draw
 * a single ranked field. `auditLdsg()` asserts the invariant and is called in
 * the tests after every turn of a real race, because a lapped car that drifts
 * above a lead-lap car is a bug the eye would never catch.
 *
 * ── THREE PLACES THE SOURCES CONTRADICT THEMSELVES, AND HOW EACH IS RULED ───
 *
 * **D-EZ-12 — a SUCCESSFUL repair puts a car a lap down. A failed one is still
 * out of the race.** EZRA says both things in two different sections:
 *
 *   Laps Down Procedure: "the 'laps down' sub-group only begins to be populated
 *     when there is TROUBLE or PROBLEM events, followed by SUCCESSFUL REPAIR
 *     rolls."
 *   §3.11 bullet:       "Anytime a driver FAILS a REPAIR roll and has to enter
 *     the Pits for repairs, they are placed at the end of the LDSG."
 *
 * Ruled for "successful", on three legs. (1) The section actually *titled* for
 * this rule says successful, and it is a near-verbatim reprint of Zimmerman's
 * sheet, which also says successful — two independent documents agree. (2) The
 * §3.11 wording contradicts itself: RB/iv is explicit that on a failed repair
 * "the car cannot be repaired, and is out of the race", so a car that failed
 * does NOT "enter the Pits for repairs" — that clause describes a success.
 * (3) The rejected reading would silently delete the base game's entire
 * attrition model without a word about it, and an add-on does not overturn the
 * rulebook by implication. Measured 4 Oct 2026 over 360 season races (ten
 * seeds, the printed game — every house rule off): 7.6 DNFs a race, 3.6 of them
 * PROBLEM (mechanical), 3.3 TROUBLE, 0.7 feud. (The old "~4.4 a race" here was
 * stale; the measured figure had been 8.4–8.6 before D-EZ-88/89.) The rejected
 * reading, applied to PROBLEM repairs only, is now house rule D-EZ-87's modern
 * era — switchable, default on — and with every house rule on it measures 5.2
 * DNFs a race, 0.9 of them mechanical. Re-measure with `npm run laps-down`.
 *
 * **D-EZ-13 — the Lucky Dog is awarded at the START of the yellow.** Again two
 * readings: the dedicated Lucky Dog Rule says "implemented at the beginning of
 * the Yellow Flag Turn", while §3.11's summary bullet says "when a Yellow Flag
 * period (1 or 2 turns) ends". The specific section governs over the summary,
 * and it matches the real-world procedure the rule is named for. Consecutive
 * yellow turns count as ONE event, so a two-turn caution awards one pass.
 *
 * **D-EZ-14 — no wave-around by default.** Zimmerman prints a per-grade
 * wave-around die (A 4-or-less … D 1-or-less, E never) on top of the Lucky Dog.
 * EZRA-S2 has no wave-around at all, and adds qualifiers Zimmerman lacks. D-EZ-1
 * settled that EZRA governs where the two overlap, so the wave-around ships
 * **off**, behind `waveAround: true`, with the printed table intact so turning
 * it on is a switch and not a rewrite. CROSS_REFERENCE C15 is the record.
 *
 * ── THE PIT CREW LADDER, ATTESTED TWICE ────────────────────────────────────
 *
 * Zimmerman floated a second, speculative PIT CREW gate for the follow-up
 * challenge ("Perhaps a PIT CREW grade of A or B would be required…") and
 * flagged it as unresolved. EZRA-S2 ratifies the first ladder verbatim and
 * settles the second gate **differently** — on a STAR, not on the crew grade:
 *
 *   > "If the LDSG Challenger wins the challenge, the driver moves to the last
 *   >  position in the LLSG, subject to Pit crew rating (A-B-C automatic, D has
 *   >  to successfully Challenge twice, E can never challenge out of the LDSG).
 *   >  A successful LDSG challenger who has a Star on the indicated Quality of
 *   >  the turn may make a second challenge into the Middle Group. (RB vii)"
 *
 * So the speculative gate is not implemented, and CROSS_REFERENCE A23 records
 * why. A rule an author marked "not etched in stone" is not a rule.
 */

import type { Car, PitCrewGrade } from './types.ts';
import type { Dice } from './dice.ts';
import type { DiceRecord, PositionDelta } from './ruleEvent.ts';
import { parkChips, scopeChips } from './chips.ts';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_LD = {
  procedure:
    'EZRA-S2, "Laps Down Procedure" — "At the start of a race, all BOTTOM group drivers are on the ' +
    'lead lap and there is only ONE group. A second group, the “laps down” sub-group only begins to ' +
    'be populated when there is TROUBLE or PROBLEM events, followed by successful REPAIR rolls."',
  suffix:
    'RWBR_laps_down.pdf (Zimmerman) — "Doing it this way will preserve the game’s “King of the ' +
    'Hill” methodology, in that cars are ranked in order at all times during the race."',
  challenge:
    'EZRA-S2, "Laps Down Procedure" — "when a driver in the “laps down” sub-group wins a challenge, ' +
    'instead of exchanging places with the MIDDLE group driver, his card is moved to the last open ' +
    'position of the “lead lap” sub-group of the BOTTOM group."',
  order:
    'EZRA-S2, "Laps Down Procedure" — "Any BOTTOM group challenge result always includes both ' +
    'sub-groups, starting with the “laps down” sub-group, and moving to the “lead lap” sub-group."',
  ladder:
    'EZRA-S2 §3.11 — "subject to Pit crew rating (A-B-C automatic, D has to successfully Challenge ' +
    'twice, E can never challenge out of the LDSG)."',
  starSecond:
    'EZRA-S2 §3.11 — "A successful LDSG challenger who has a Star on the indicated Quality of the ' +
    'turn may make a second challenge into the Middle Group. (RB vii)"',
  luckyDog:
    'EZRA-S2, "Lucky Dog Rule" — "Whenever a YELLOW flag comes out, the driver occupying the first ' +
    'position in the “laps down” sub-group becomes the “Lucky Dog” and automatically becomes the ' +
    'last position of the “lead lap” sub-group." / "The Lucky Dog free pass is implemented at the ' +
    'beginning of the Yellow Flag Turn."',
  luckyDogWait:
    'EZRA-S2, "Lucky Dog Rule" — "If the Yellow flag comes out in the turn immediately following the ' +
    'turn during which a driver is designated a lap down, he does not benefit from the Lucky Dog ' +
    'pass. The driver must spend at least one complete turn in lap down status."',
  luckyDogCause:
    'EZRA-S2 §3.11 — "If the driver entered the LDSG as part of the incident that caused the Yellow ' +
    'Flag that driver will NOT be awarded a “Lucky Dog” pass."',
  luckyDogOnce:
    'EZRA-S2, "Lucky Dog Rule" — "For purposes of the Lucky Dog optional rule, consecutive yellow ' +
    'flag turns count as one yellow flag event."',
  waveAround:
    'RWBR_laps_down.pdf (Zimmerman) — the per-PIT-CREW wave-around die. NOT an EZRA rule; off by ' +
    'default under D-EZ-14, because EZRA-S2 governs where the two sheets overlap (D-EZ-1, C15).',
  noDraft: 'RWBR_MH_drafting.pdf — "Lap down Cars cannot join a Draft."',
} as const;

/**
 * D-EZ-12. `false` is the ruling in force: a SUCCESSFUL repair goes a lap down.
 * Flip it and a FAILED repair goes a lap down instead of out of the race, which
 * is EZRA §3.11's literal wording. Exported so the harness can measure both and
 * so the rejected reading is a switch rather than a rewrite.
 */
export const LDSG_ON_FAILED_REPAIR = false;

// ---------------------------------------------------------------------------
// The boundary
// ---------------------------------------------------------------------------

/**
 * The 1-based position of the first car in the LDSG.
 *
 * Returns `order.length + 1` when nobody is a lap down — i.e. the boundary sits
 * past the end of the field, which is exactly the state every race starts in.
 * Computed from the FIRST lapped car rather than by counting them, so that a
 * broken invariant surfaces as a wrong boundary instead of being averaged away.
 */
export function ldsgStart(order: readonly Car[]): number {
  const i = order.findIndex((c) => c.lapsDown);
  return i === -1 ? order.length + 1 : i + 1;
}

/** Is this car a lap down? */
export const isLapped = (car: Pick<Car, 'lapsDown'>): boolean => car.lapsDown === true;

/** How many cars are a lap down. */
export const lappedCount = (order: readonly Car[]): number => order.filter(isLapped).length;

/**
 * Check the suffix invariant and return every violation in plain words.
 *
 * Empty means the field is sound. This is asserted after every turn of a real
 * race in the tests, not merely on fixtures, because the way this breaks is a
 * legal-looking swap in some unrelated module three months from now.
 */
export function auditLdsg(order: readonly Car[]): string[] {
  const problems: string[] = [];
  let seenLapped = false;
  for (let i = 0; i < order.length; i++) {
    const car = order[i];
    if (isLapped(car)) {
      seenLapped = true;
    } else if (seenLapped) {
      problems.push(
        `${car.card.driverId} is on the lead lap at P${i + 1} but sits BELOW a lapped car — ` +
          'the LDSG must be a suffix of the running order',
      );
    }
  }
  return problems;
}

// ---------------------------------------------------------------------------
// Moving cars across the boundary
// ---------------------------------------------------------------------------

/**
 * Put a car a lap down: to the very back of the field, flagged.
 *
 * Chips go with it. RB/xv forbids carrying ORDINARY chips in the BOTTOM group at
 * all, so a car arriving in the LDSG has none by definition — stripped here
 * rather than relying on `renumber` to catch it, because the strip must happen
 * at the moment of the move for the ledger to read truthfully. Chips a driver
 * TRAIT awarded are parked instead of destroyed (EZRA-S2 §4.0); `parkChips`
 * knows the difference so this function does not have to.
 */
export function sendLapDown(order: Car[], driverId: string): PositionDelta | null {
  const i = order.findIndex((c) => c.card.driverId === driverId);
  if (i === -1) return null;
  const from = i + 1;
  const [car] = order.splice(i, 1);
  order.push({ ...parkChips(car).car, lapsDown: true });
  return {
    driverId,
    from,
    to: order.length,
    placement: 'laps-down',
    note: 'a lap down',
  };
}

/**
 * Move a car to the LAST position of the lead-lap sub-group — the slot directly
 * above the boundary — and clear its lapped flag.
 *
 * This is the destination for all three ways back onto the lead lap (a won
 * challenge, the Lucky Dog, a wave-around) and also for Appendix B2's row 11,
 * "returns as the last driver on the lead lap", which until now could only be
 * approximated by the end of the field.
 *
 * Note the ordering: the car is spliced OUT first and the boundary recomputed
 * after, because removing a lapped car shifts the boundary by one and inserting
 * against a stale index puts the car on the wrong side of its own marker.
 */
export function returnToLeadLap(order: Car[], driverId: string, note: string): PositionDelta | null {
  const i = order.findIndex((c) => c.card.driverId === driverId);
  if (i === -1) return null;
  const from = i + 1;
  const [car] = order.splice(i, 1);
  const boundary = ldsgStart(order); // recomputed AFTER the removal, deliberately
  const to = Math.min(boundary, order.length + 1);
  // Back on the lead lap: if the slot is above the BOTTOM boundary, parked
  // trait chips come back to hand here (EZRA-S2 §4.0). `scopeChips` decides,
  // because only the destination position knows.
  order.splice(to - 1, 0, scopeChips({ ...car, lapsDown: false }, to).car);
  return { driverId, from, to, placement: 'last-of-lead-lap', note };
}

/**
 * Insert a car at the last lead-lap position WITHOUT touching its lapped flag.
 *
 * Used by pit results that say "the last driver on the lead lap" about a car
 * that was never a lap down in the first place. Separate from
 * `returnToLeadLap` so that neither has to guess what the caller meant.
 */
export function moveToLastOfLeadLap(order: Car[], driverId: string, note: string): PositionDelta | null {
  const i = order.findIndex((c) => c.card.driverId === driverId);
  if (i === -1) return null;
  if (order[i].lapsDown) return returnToLeadLap(order, driverId, note);
  const from = i + 1;
  const [car] = order.splice(i, 1);
  const boundary = ldsgStart(order);
  const to = Math.min(boundary, order.length + 1);
  order.splice(to - 1, 0, scopeChips(car, to).car);
  return { driverId, from, to, placement: 'last-of-lead-lap', note };
}

// ---------------------------------------------------------------------------
// The boundary clamp
// ---------------------------------------------------------------------------

/**
 * Re-settle the field so that every lapped car sits below every lead-lap car,
 * keeping the relative order inside each sub-group exactly as it was.
 *
 * ── WHY THIS IS NEEDED AT ALL ──────────────────────────────────────────────
 *
 * Placement rules can be taught the boundary. **Swaps cannot.** The pit turn's
 * box re-order (RB/xi) exchanges two cars' positions outright, and it was
 * happily trading a car in the LDSG for a car on the lead lap — measured, on
 * the very first run of `npm run laps-down`, as a lapped car climbing to P10
 * while a lead-lap car dropped to P18. Every test passed. The audit found it in
 * one race, which is the entire argument for having an invariant a machine can
 * check.
 *
 * ── CLAMP, NOT VOID, AND WHY THAT READING ──────────────────────────────────
 *
 * There are two ways to refuse a move that crosses the boundary: void it (both
 * cars stay put) or clamp it (each car goes as far as it may and stops at the
 * marker). EZRA settles this where it has to place a penalised car with the
 * split in force — restart violations send offenders "to the last position in
 * the Bottom Group Lead Lap Sub Group", i.e. **to the boundary**, not back to
 * where they started. A stable partition IS that clamp, applied to the whole
 * field at once: a lapped car moving up stops at the marker, a lead-lap car
 * moving down stops at the marker, and neither loses the ground it fairly made
 * inside its own sub-group.
 *
 * The physical reading agrees. A car a lap down cannot gain a position on a
 * lead-lap car by servicing faster; it is a lap behind on the road. The board
 * position is a proxy for track position and the marker is what says so.
 *
 * `moved` is returned rather than swallowed. A clamp that silently corrects
 * things is indistinguishable from a bug that silently happens, so the count is
 * carried on the tracker and printed by the harness.
 */
export function settleLapsDown(order: readonly Car[]): { order: Car[]; moved: number } {
  const lead: Car[] = [];
  const lapped: Car[] = [];
  for (const car of order) (isLapped(car) ? lapped : lead).push(car);
  if (lapped.length === 0 || lead.length === 0) return { order: [...order], moved: 0 };

  const settled = [...lead, ...lapped];
  let moved = 0;
  for (let i = 0; i < settled.length; i++) {
    if (settled[i].card.driverId !== order[i].card.driverId) moved++;
  }
  return { order: settled, moved };
}

// ---------------------------------------------------------------------------
// The PIT CREW ladder
// ---------------------------------------------------------------------------

/**
 * How many won challenges it takes to climb out of the LDSG.
 *
 * `null` = never. Attested twice, identically, in Zimmerman and EZRA-S2 §3.11.
 */
export const ESCAPE_CHALLENGES: Readonly<Record<PitCrewGrade, number | null>> = Object.freeze({
  A: 1,
  B: 1,
  C: 1,
  D: 2,
  E: null,
});

/**
 * May this car challenge out of the LDSG at all?
 *
 * READING: a grade-E car does not make the challenge, rather than making one
 * that is discarded. EZRA's wording is "E can never CHALLENGE OUT of the LDSG"
 * — not "never escapes" — and Zimmerman's is "would not be able to escape …
 * this driver would simply 'limp around the track' until the race ended". A roll
 * whose only possible outcome is nothing is not a roll the printed rules ask
 * for, and rolling it would consume dice and desynchronise every seeded race.
 */
export const canChallengeOutOfLdsg = (grade: PitCrewGrade): boolean => ESCAPE_CHALLENGES[grade] !== null;

/** The Zimmerman wave-around die, by PIT CREW grade. `null` = cannot return. */
export const WAVE_AROUND_MAX: Readonly<Record<PitCrewGrade, number | null>> = Object.freeze({
  A: 4,
  B: 3,
  C: 2,
  D: 1,
  E: null,
});

// ---------------------------------------------------------------------------
// Race-long bookkeeping
// ---------------------------------------------------------------------------

/**
 * What a race has to remember about being a lap down.
 *
 * Race-scoped and mutable, the same shape as the Chart B tracker: owned by
 * `race.ts`, handed to each turn, never reconstructed mid-race. Three of the
 * four printed rules here are *memory* rules — "has to challenge twice", "must
 * spend at least one complete turn", "consecutive yellows count as one event" —
 * and none of them can be answered from the board alone.
 */
export class LapsDownTracker {
  /** Won LDSG challenges banked, for grade D's "successfully Challenge twice". */
  private readonly wins = new Map<string, number>();
  /** The turn on which each car went a lap down, for the one-complete-turn rule. */
  private readonly downOn = new Map<string, number>();
  /** Cars that went down as part of the incident that brought out the current yellow. */
  private causedThisCaution = new Set<string>();
  /** True once this caution has spent its single Lucky Dog. */
  private luckyDogSpent = false;
  /** Was the previous turn already yellow? Distinguishes a new caution from turn 2 of one. */
  private prevWasYellow = false;
  /** Cards the boundary clamp had to put back. Surfaced, never swallowed. */
  private clamped = 0;

  /** Record a car arriving in the LDSG on `turn`. */
  wentDown(driverId: string, turn: number, causedTheCaution = false): void {
    this.downOn.set(driverId, turn);
    this.wins.delete(driverId);
    if (causedTheCaution) this.causedThisCaution.add(driverId);
  }

  /** Record cards the boundary clamp moved this turn. */
  noteClamped(n: number): void {
    this.clamped += n;
  }

  /** How many cards the clamp has had to put back all race. */
  get clampedCards(): number {
    return this.clamped;
  }

  /** Record a car leaving the LDSG by any route. */
  cameBack(driverId: string): void {
    this.downOn.delete(driverId);
    this.wins.delete(driverId);
    this.causedThisCaution.delete(driverId);
  }

  /**
   * Bank a won LDSG challenge. Returns true when the car has now won enough to
   * climb out. A grade that can never escape banks nothing.
   */
  bankWin(driverId: string, grade: PitCrewGrade): boolean {
    const needed = ESCAPE_CHALLENGES[grade];
    if (needed === null) return false;
    const now = (this.wins.get(driverId) ?? 0) + 1;
    this.wins.set(driverId, now);
    if (now >= needed) {
      this.wins.delete(driverId);
      return true;
    }
    return false;
  }

  /** Wins banked so far — for the ledger line, so "one down, one to go" can be said. */
  winsBanked(driverId: string): number {
    return this.wins.get(driverId) ?? 0;
  }

  /**
   * Tell the tracker what kind of turn is starting. Must be called once per turn,
   * before anything else, because "consecutive yellow turns count as one yellow
   * flag event" is the difference between one Lucky Dog and two.
   */
  startTurn(isYellow: boolean): void {
    if (isYellow && !this.prevWasYellow) {
      // A NEW caution: this one gets a Lucky Dog.
      this.luckyDogSpent = false;
    }
    if (!isYellow) {
      // Green again — whoever caused the last caution is no longer marked.
      this.causedThisCaution = new Set();
    }
    this.prevWasYellow = isYellow;
  }

  /** Has this caution already awarded its pass? */
  get luckyDogAlreadySpent(): boolean {
    return this.luckyDogSpent;
  }

  /** Mark this caution's pass as spent. */
  spendLuckyDog(): void {
    this.luckyDogSpent = true;
  }

  /**
   * Is this car eligible for the Lucky Dog on a yellow starting at `turn`?
   *
   * Two printed bars: he must have spent at least one complete turn down, and he
   * must not have gone down in the incident that brought the yellow out. Both are
   * the same idea from different directions — the pass is for cars the caution
   * caught out, not for cars the caution is *about*.
   */
  luckyDogEligible(driverId: string, turn: number): { ok: boolean; why: string } {
    if (this.causedThisCaution.has(driverId)) {
      return { ok: false, why: 'he went a lap down in the incident that brought this yellow out' };
    }
    const down = this.downOn.get(driverId);
    if (down === undefined) return { ok: false, why: 'not in the laps-down sub-group' };
    if (turn - down < 1) {
      return { ok: false, why: 'he has not yet spent a complete turn a lap down' };
    }
    return { ok: true, why: 'first in the laps-down sub-group, and has run a full turn there' };
  }
}

// ---------------------------------------------------------------------------
// The Lucky Dog
// ---------------------------------------------------------------------------

export interface LuckyDogResult {
  driverId: string | null;
  delta: PositionDelta | null;
  /** Every laps-down car considered, in order, with the verdict on each. */
  considered: { driverId: string; ok: boolean; why: string }[];
  description: string;
  ruleCitation: string;
}

/**
 * Award the Lucky Dog at the start of a yellow-flag turn.
 *
 * Walks the LDSG from the front and takes the first car that clears both bars,
 * rather than testing only the leader of the LDSG and giving up. EZRA names "the
 * driver occupying the first position", but it also disqualifies that very driver
 * when he caused the caution — and a rule cannot both name a recipient and
 * disqualify him and mean that nobody gets the pass. The walk is the reading that
 * makes both sentences true at once.
 */
export function awardLuckyDog(
  order: Car[],
  tracker: LapsDownTracker,
  turn: number,
): LuckyDogResult {
  const none = (description: string): LuckyDogResult => ({
    driverId: null,
    delta: null,
    considered: [],
    description,
    ruleCitation: CITE_LD.luckyDog,
  });

  if (tracker.luckyDogAlreadySpent) {
    return none('The Lucky Dog was already awarded on this caution — consecutive yellow turns are one event.');
  }
  const lapped = order.filter(isLapped);
  if (lapped.length === 0) return none('Nobody is a lap down; there is no Lucky Dog to award.');

  const considered: { driverId: string; ok: boolean; why: string }[] = [];
  for (const car of lapped) {
    const id = car.card.driverId;
    const verdict = tracker.luckyDogEligible(id, turn);
    considered.push({ driverId: id, ...verdict });
    if (!verdict.ok) continue;

    const name = car.card.name ?? id;
    const delta = returnToLeadLap(order, id, 'Lucky Dog');
    tracker.cameBack(id);
    tracker.spendLuckyDog();
    return {
      driverId: id,
      delta,
      considered,
      description:
        `The yellow is out and ${name} is the Lucky Dog — first car a lap down, and he gets his ` +
        `lap back into the last spot on the lead lap.`,
      ruleCitation: CITE_LD.luckyDog,
    };
  }

  const first = considered[0];
  return {
    driverId: null,
    delta: null,
    considered,
    description: `No Lucky Dog on this caution: ${first.why}.`,
    ruleCitation: `${CITE_LD.luckyDog}; ${CITE_LD.luckyDogCause}; ${CITE_LD.luckyDogWait}`,
  };
}

// ---------------------------------------------------------------------------
// The wave-around (Zimmerman, OFF by default — D-EZ-14)
// ---------------------------------------------------------------------------

export interface WaveAroundResult {
  driverId: string;
  grade: PitCrewGrade;
  roll: number | null;
  waved: boolean;
  delta: PositionDelta | null;
  dice: DiceRecord[];
}

/**
 * Everyone still a lap down after the Lucky Dog rolls one die to be waved around.
 *
 * Zimmerman's rule, and not EZRA's — see D-EZ-14. Rolled front-to-back so that
 * the order cars re-enter the lead lap in matches the order they were running,
 * which is the only ordering the sheet's "highest-ranked driver" language
 * implies anywhere.
 */
export function waveAround(
  order: Car[],
  tracker: LapsDownTracker,
  dice: Dice,
): WaveAroundResult[] {
  const results: WaveAroundResult[] = [];
  for (const car of order.filter(isLapped)) {
    const id = car.card.driverId;
    const grade = car.card.pitCrewGrade;
    const max = WAVE_AROUND_MAX[grade];
    if (max === null) {
      results.push({ driverId: id, grade, roll: null, waved: false, delta: null, dice: [] });
      continue;
    }
    const r = dice.d6();
    const waved = r <= max;
    const record: DiceRecord = {
      kind: 'd6',
      dice: [r],
      total: r,
      label: `wave-around, grade ${grade} (${max} or less)`,
    };
    let delta: PositionDelta | null = null;
    if (waved) {
      delta = returnToLeadLap(order, id, 'waved around');
      tracker.cameBack(id);
    }
    results.push({ driverId: id, grade, roll: r, waved, delta, dice: [record] });
  }
  return results;
}
